import React, { useMemo, useState, useCallback, useRef } from 'react';
import VendorAutocompleteInput from './VendorAutocompleteInput';
import { HelpInfoButton } from './HelpTooltip';
import { effectiveClassification } from '../utils/importDedup';
import {
  CLASSIFICATIONS,
  describeLanding,
  summarizeLandings,
  isDeclinedImportRow,
} from '../utils/importLanding';
import {
  buildImportSummary,
  groupRelationshipCards,
  importOriginLookup,
  relationshipCardModel,
} from '../utils/importSummary';

const ALL_EDITABLE_COLUMNS = [
  { key: 'description', label: 'Description', type: 'text', minWidth: 160 },
  { key: 'category',    label: 'Category',    type: 'text', minWidth: 100 },
  { key: 'location',    label: 'Area',        type: 'text', minWidth: 90  },
  { key: 'vendor',      label: 'Vendor',      type: 'vendor', minWidth: 120 },
  { key: 'unit',        label: 'Unit',        type: 'text', minWidth: 68  },
  { key: 'case_quantity', label: 'Case Qty',  type: 'number', minWidth: 72 },
  { key: 'case_price',  label: 'Case $',      type: 'decimal', minWidth: 80 },
];

// The review answers two questions — "are these products right?" and "are the
// relationships right?" — so a row's STATUS is a badge on the item itself, not
// an implementation-detail column the reader has to decode.
const BADGES = {
  new: { label: 'New item', bg: '#C8E6C9', color: '#2E7D32' },
  price_change: { label: 'Price change', bg: '#FFECB3', color: '#B26A00' },
  // A line that joins an item you ALREADY count, as another way to buy it.
  new_option: { label: 'New vendor option', bg: '#BBDEFB', color: '#1565C0' },
  unchanged: { label: 'No change', bg: '#E0E0E0', color: '#616161' },
  // Explicitly left out. Kept visible so a rejection reads as a decision
  // rather than as "there was nothing to do".
  rejected: { label: 'Not imported', bg: '#F8D3D0', color: '#B71C1C' },
};
const NEEDS_REVIEW_BADGE = { label: 'Needs review', bg: '#FFE0B2', color: '#E65100' };

// Relationship warnings are informational, not errors — they describe that
// Countertop noticed two products may be ways to buy the same inventory need.
// They must not make the primary item review look like it needs attention.
function isRelationshipWarning(text) {
  if (!text || typeof text !== 'string') return false;
  return text.includes('Another way to buy')
    || text.includes('count separately')
    || text.includes('similar item to')
    || text.includes('already looks like');
}
function realWarningsForLanding(landing) {
  const warnings = landing?.warnings || [];
  return warnings.filter((w) => !isRelationshipWarning(w));
}

// A document import reviews vendor products, not only canonical inventory
// items. Several rows may therefore share item.id; `_review_key` keeps their
// edits, selections and React keys independent.
function reviewRowKey(item) {
  return item?._review_key ?? item?.id;
}

function fmtPrice(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '—';
}

function fmtDelta(abs, pct) {
  if (abs == null) return null;
  const sign = abs >= 0 ? '+' : '';
  const pctStr = pct != null ? ` (${sign}${pct.toFixed(1)}%)` : '';
  return `${sign}$${Math.abs(abs).toFixed(2)}${pctStr}`;
}

function InlineCell({ col, item, onUpdate, storeId }) {
  if (col.type === 'vendor') {
    return (
      <VendorAutocompleteInput
        value={item[col.key] || ''}
        onChange={(v) => onUpdate(reviewRowKey(item), col.key, v)}
        storeId={storeId}
        className=""
        placeholder="Vendor"
        inputProps={{
          style: {
            width: '100%',
            padding: '0.28rem 0.4rem',
            border: '1px solid #E0D5CC',
            borderRadius: 4,
            fontSize: '0.8rem',
            background: '#fff',
            boxSizing: 'border-box',
          },
        }}
      />
    );
  }
  return (
    <input
      type={col.type === 'number' || col.type === 'decimal' ? 'number' : 'text'}
      step={col.type === 'decimal' ? '0.01' : undefined}
      value={item[col.key] ?? ''}
      onChange={(e) => {
        let val = e.target.value;
        if (col.type === 'number') val = parseInt(val, 10) || 0;
        else if (col.type === 'decimal') val = parseFloat(val) || 0;
        onUpdate(reviewRowKey(item), col.key, val);
      }}
      style={{
        width: '100%',
        padding: '0.28rem 0.4rem',
        border: '1px solid #E0D5CC',
        borderRadius: 4,
        fontSize: '0.8rem',
        background: '#fff',
        boxSizing: 'border-box',
      }}
    />
  );
}

function ClassificationSelector({ item, onReclassify, allowVendorProduct }) {
  if (!onReclassify) return null;
  const hasExistingMatch = !!item._existingItem;
  const current = effectiveClassification(item);
  const overridden = !!item._classificationOverride;

  const options = [{ value: 'new', label: '+ New item' }];
  // Only allow "Price change" when there's actually an existing item to update.
  // (Unchanged items always have a matched existing item; new items don't.)
  if (hasExistingMatch) {
    options.push({ value: 'price_change', label: '↕ Price change' });
    // Attaching to an existing item only makes sense when there IS one, and
    // only in flows whose save path knows how to write a purchase option.
    if (allowVendorProduct) {
      options.push({ value: 'new_option', label: '⇄ Another way to buy it' });
    }
  }
  options.push({ value: 'unchanged', label: '✓ Skip / unchanged' });
  options.push({ value: 'rejected', label: "✕ Don't import" });

  const badge = BADGES[current] || BADGES.unchanged;

  return (
    <select
      value={current}
      onChange={(e) => onReclassify(reviewRowKey(item), e.target.value)}
      title={overridden ? 'Manually overridden — click to change' : 'Change how this item will be imported'}
      style={{
        fontSize: '0.75rem',
        padding: '0.25rem 0.35rem',
        borderRadius: 6,
        border: overridden ? `1.5px solid ${badge.color}` : '1px solid #D8C8B8',
        background: overridden ? badge.bg : '#fff',
        color: overridden ? badge.color : '#5A4A42',
        fontWeight: 600,
        cursor: 'pointer',
        minWidth: 140,
        outline: 'none',
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}{overridden && o.value === current ? ' *' : ''}
        </option>
      ))}
    </select>
  );
}

function StatusBadge({ badge, testId }) {
  return (
    <span
      data-testid={testId || 'row-badge'}
      style={{
        display: 'inline-block',
        background: badge.bg,
        color: badge.color,
        borderRadius: 4,
        padding: '0.05rem 0.4rem',
        fontSize: '0.62rem',
        fontWeight: 800,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {badge.label}
    </span>
  );
}

/**
 * What one package IS, said only from facts the row states. A pack description
 * is the vendor's own wording; without one, the bare unit is all we can claim —
 * inventing "N × unit" from case_quantity here would assert pack semantics this
 * screen has no authority over (packText owns that).
 */
function purchaseUnitText(item) {
  const pack = String(item?.pack_size ?? '').trim();
  if (pack) return pack;
  const unit = String(item?.unit ?? '').trim();
  return unit || '—';
}

/**
 * The editor drawer's label for a column, said in the row's own terms.
 *
 * "Case $" is a lie on most of a bar's order guide. A 1 L bottle of Bacardi
 * bought singly stores case_quantity 1 and case_price 19.95 — arithmetically
 * right, and read by everyone who saw it as "$19.95 a case" for a bottle that
 * costs $19.95. The stored field means the price of ONE PURCHASE PACKAGE, so
 * the label names the package this row actually describes and the reader never
 * has to guess which tier they are editing.
 *
 * Only the package-tier fields are renamed; every other column keeps whatever
 * the host called it. The catalog's CSV export still writes "Case $" — that
 * header is a round-trip contract with the importer and is not this screen's
 * to change.
 */
function columnLabel(col, item) {
  const pack = purchaseUnitText(item);
  const unit = String(item?.unit ?? '').trim();
  if (col.key === 'case_price') {
    return pack === '—' ? 'Price per package' : `Price per ${pack}`;
  }
  if (col.key === 'case_quantity') {
    // Deliberately not "${unit}s per package": the units here are mass and
    // count nouns alike ("lb", "each", "oz"), and there is no pluralization
    // rule that reads right for all of them. The Unit field sits two cells
    // away in this same drawer and names it.
    return 'Units per package';
  }
  if (col.key === 'unit_price') {
    return unit ? `Price per ${unit}` : col.label;
  }
  return col.label;
}

// Hosts coerce an unknown price to 0 before the review opens, so a 0 here
// almost always means "no price on the source line" — shown as —, never as
// $0.00 (the drawer's editable field still holds the raw value).
function rowPriceValue(item) {
  const casePrice = parseFloat(item?.case_price);
  if (Number.isFinite(casePrice) && casePrice !== 0) return casePrice;
  const unitPrice = parseFloat(item?.unit_price);
  if (Number.isFinite(unitPrice) && unitPrice !== 0) return unitPrice;
  return null;
}

/** The compact review row plus its expandable editor drawer. */
function ProductRow({
  item,
  landing,
  classification,
  expanded,
  onToggleExpanded,
  onToggleImport,
  onUpdateItem,
  onReclassify,
  storeId,
  columns,
  showPrice,
  countTargets,
  onToggleCountTarget,
  landingContext,
  allowVendorProduct,
  helpPage,
  colCount,
}) {
  const key = reviewRowKey(item);
  const importing = !isDeclinedImportRow(item);
  const realWarnings = realWarningsForLanding(landing);
  const needsReview = realWarnings.length > 0;
  const badge = BADGES[classification] || BADGES.new;
  const isPriceChange = classification === CLASSIFICATIONS.PRICE_CHANGE;
  const delta = isPriceChange ? fmtDelta(item._priceDeltaAbsolute, item._priceDeltaPct) : null;
  const deltaColor = (item._priceDeltaAbsolute ?? 0) > 0 ? '#C62828' : '#2E7D32';
  const name = String(item.description ?? '').trim() || 'Unnamed item';
  const dimmed = !importing;

  return (
    <>
      <tr
        data-testid="product-row"
        style={{
          background: expanded ? '#FBF6F0' : '#FFFDF9',
          borderBottom: expanded ? 'none' : '1px solid #EFE4DA',
          opacity: dimmed ? 0.55 : 1,
        }}
      >
        <td style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top', textAlign: 'center', width: 40 }}>
          <input
            type="checkbox"
            checked={importing}
            disabled={!onReclassify}
            onChange={(e) => onToggleImport(item, e.target.checked)}
            style={{ cursor: onReclassify ? 'pointer' : 'not-allowed', accentColor: '#526B7C', width: 15, height: 15 }}
            aria-label={`Import ${name}`}
          />
        </td>
        <td style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top' }}>
          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#5A4A42', lineHeight: 1.3 }}>
            {name}
          </div>
          {/* On phones the vendor/unit/price columns collapse into this line
              (media query below) — the table must fit the screen, never
              scroll sideways. */}
          <div className="iat-meta-inline" style={{ fontSize: '0.72rem', color: '#7a6a62', lineHeight: 1.35, marginTop: 2 }}>
            {[
              String(item.vendor ?? '').trim() || null,
              purchaseUnitText(item) !== '—' ? purchaseUnitText(item) : null,
              showPrice
                ? (isPriceChange && item._existingItem
                  ? `${fmtPrice(item._existingItem.case_price)} → ${fmtPrice(rowPriceValue(item))}`
                  : (rowPriceValue(item) != null ? fmtPrice(rowPriceValue(item)) : null))
                : null,
            ].filter(Boolean).join(' · ')}
          </div>
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginTop: 3, alignItems: 'center' }}>
            <StatusBadge badge={badge} />
            {needsReview && <StatusBadge badge={NEEDS_REVIEW_BADGE} testId="needs-review-badge" />}
          </div>
          {needsReview && (
            <div
              data-testid="row-warning"
              style={{ fontSize: '0.72rem', color: '#E65100', fontWeight: 600, lineHeight: 1.3, marginTop: 3, maxWidth: 420 }}
            >
              {realWarnings[0]}
            </div>
          )}
        </td>
        <td className="iat-col-secondary" style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top', fontSize: '0.8rem', color: '#5A4A42' }}>
          {String(item.vendor ?? '').trim() || '—'}
        </td>
        <td className="iat-col-secondary" style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top', fontSize: '0.8rem', color: '#5A4A42' }}>
          {purchaseUnitText(item)}
        </td>
        {showPrice && (
          <td className="iat-col-secondary" style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
            {isPriceChange && item._existingItem ? (
              <div>
                <span style={{ fontSize: '0.74rem', color: '#888', textDecoration: 'line-through' }}>
                  {fmtPrice(item._existingItem.case_price)}
                </span>
                <span style={{ fontSize: '0.76rem', color: '#333', fontWeight: 600, margin: '0 0.25rem' }}>→</span>
                <span style={{ fontSize: '0.8rem', color: '#5A4A42', fontWeight: 700 }}>
                  {fmtPrice(rowPriceValue(item))}
                </span>
                {delta && (
                  <div style={{ fontSize: '0.68rem', color: deltaColor, fontWeight: 600 }}>{delta}</div>
                )}
              </div>
            ) : (
              <span style={{ fontSize: '0.8rem', color: '#5A4A42', fontWeight: 600 }}>
                {fmtPrice(rowPriceValue(item))}
              </span>
            )}
          </td>
        )}
        <td style={{ padding: '0.4rem 0.5rem', verticalAlign: 'top', textAlign: 'right', width: 60 }}>
          <button
            type="button"
            onClick={() => onToggleExpanded(key)}
            aria-expanded={expanded}
            aria-label={`Edit ${name}`}
            style={{
              background: 'none',
              border: '1px solid #D8C8B8',
              borderRadius: 6,
              color: '#526B7C',
              fontSize: '0.72rem',
              fontWeight: 700,
              cursor: 'pointer',
              padding: '0.22rem 0.55rem',
              whiteSpace: 'nowrap',
            }}
          >
            {expanded ? 'Close' : 'Edit'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr data-testid="row-editor" style={{ background: '#FBF6F0', borderBottom: '1px solid #EFE4DA' }}>
          <td />
          <td colSpan={colCount - 1} style={{ padding: '0.25rem 0.5rem 0.8rem' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '0.5rem 0.75rem',
              marginBottom: '0.6rem',
            }}>
              {columns.map((col) => (
                <label key={col.key} style={{ display: 'block', fontSize: '0.68rem', fontWeight: 700, color: '#7a6a62' }}>
                  <span style={{ display: 'block', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{columnLabel(col, item)}</span>
                  <InlineCell col={col} item={item} onUpdate={onUpdateItem} storeId={storeId} />
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a6a62' }}>Import as:</span>
              <ClassificationSelector item={item} onReclassify={onReclassify} allowVendorProduct={allowVendorProduct} />
              {helpPage && <HelpInfoButton helpTopicId="import.review_actions" page={helpPage} section="review" />}
            </div>
            {/* The plain-English consequence of approving this row — nobody
                should have to infer "this becomes a second item I count"
                from a badge color. */}
            <div style={{ background: '#FFFDF9', border: '1px solid #EFE4DA', borderRadius: 8, padding: '0.5rem 0.65rem' }}>
              <div data-testid="landing-title" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#5A4A42', lineHeight: 1.25 }}>
                {landing.title}
                {helpPage && <HelpInfoButton helpTopicId="import.how_it_lands" page={helpPage} section="review" />}
              </div>
              {landing.detail && (
                <div data-testid="landing-detail" style={{ fontSize: '0.72rem', color: '#7a6a62', lineHeight: 1.35, marginTop: 2 }}>
                  {landing.detail}
                </div>
              )}
              {realWarningsForLanding(landing).map((warning) => (
                <div
                  key={warning}
                  data-testid="landing-warning"
                  style={{ fontSize: '0.72rem', color: '#E65100', fontWeight: 600, lineHeight: 1.35, marginTop: 3 }}
                >
                  <span aria-hidden="true">⚠ </span>{warning}
                </div>
              ))}
            </div>
            {classification === 'new' && countTargets.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7a6a62' }}>
                  Add to counts
                  {helpPage && <HelpInfoButton helpTopicId="import.add_to_counts" page={helpPage} section="review" />}
                </span>
                <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginTop: 3 }}>
                  {countTargets.map((t) => (
                    <label key={t.session.id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.74rem', color: '#2E7D32', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={t.selected}
                        onChange={() => onToggleCountTarget(item.id, t.session.id)}
                        style={{ cursor: 'pointer', accentColor: '#2E7D32' }}
                      />
                      {t.session.name || `Count #${t.session.id}`}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── The summary above the review ─────────────────────────────────────────────

/**
 * What the import found, stated once, above the evidence for it.
 *
 * This replaces a separate summary SCREEN that ran before the review and could
 * not be checked against anything — its two numbers came from the file tallies
 * while the review's came from the rows, and on a first import the two told
 * flatly contradictory stories about whether the store already stocked these
 * products. Every figure here is derived from the rows underneath it.
 *
 * Rendered only when the host supplies `summary`; the flows that reuse this
 * table for a menu import or a price update get the table exactly as before.
 */
function SummaryTile({ value, label, tone }) {
  return (
    <div style={{ padding: '0.5rem 0.2rem' }}>
      <div style={{
        fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.1,
        color: tone === 'quiet' ? '#9a8a82' : '#5A4A42',
      }}>
        {value}
      </div>
      <div style={{ fontSize: '0.72rem', color: '#9a8a82', fontWeight: 600, lineHeight: 1.3, marginTop: 2 }}>
        {label}
      </div>
    </div>
  );
}

function ImportSummaryHeader({ summary, numbers, showPrice, rowNounPlural }) {
  const [openVendors, setOpenVendors] = useState(false);
  const breakdown = Array.isArray(summary.vendorBreakdown) ? summary.vendorBreakdown : [];
  const tiles = [
    { key: 'found', value: numbers.itemsFound, label: `${rowNounPlural} found in your files` },
    { key: 'vendors', value: numbers.vendorCount, label: numbers.vendorCount === 1 ? 'vendor' : 'vendors' },
    { key: 'new-items', value: numbers.newItemCount, label: numbers.newItemCount === 1 ? 'new item' : 'new items' },
    { key: 'new-vendors', value: numbers.newVendorCount, label: numbers.newVendorCount === 1 ? 'new vendor' : 'new vendors' },
  ];
  if (showPrice) {
    tiles.push({
      key: 'price-updates',
      value: numbers.priceUpdateCount,
      label: numbers.priceUpdateCount === 1 ? 'price update to an item you had' : 'price updates to items you had',
    });
  }
  tiles.push({
    key: 'relationships',
    value: numbers.relationshipCount,
    label: numbers.relationshipCount === 1 ? 'relationship found' : 'relationships found',
  });

  return (
    <div
      data-testid="import-summary-header"
      style={{
        border: '1px solid #EFE4DA', borderRadius: 10, background: '#FFFDF9',
        padding: '0.8rem 1rem', marginBottom: '1rem',
      }}
    >
      <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#5A4A42', marginBottom: '0.15rem' }}>
        What this import found
      </div>
      <div style={{ fontSize: '0.76rem', color: '#9a8a82', marginBottom: '0.4rem' }}>
        Nothing below is final until you approve it.
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))',
        gap: '0.2rem 0.6rem',
      }}>
        {tiles.map((tile) => (
          <SummaryTile key={tile.key} value={tile.value} label={tile.label} tone={tile.tone} />
        ))}
      </div>
      {summary.note && (
        <div data-testid="import-summary-note" style={{
          fontSize: '0.78rem', color: '#7a6a62', lineHeight: 1.5,
          marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px dashed #EFE4DA',
        }}>
          {summary.note}
        </div>
      )}
      {breakdown.length > 0 && (
        <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px dashed #EFE4DA' }}>
          <button
            type="button"
            onClick={() => setOpenVendors((v) => !v)}
            aria-expanded={openVendors}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: '#526B7C', fontSize: '0.78rem', fontWeight: 700, fontFamily: 'inherit',
            }}
          >
            {openVendors ? 'Hide the vendor breakdown ▴' : `See all ${breakdown.length} vendors ›`}
          </button>
          {openVendors && (
            <div style={{ marginTop: '0.4rem', display: 'grid', gap: '0.15rem' }}>
              {breakdown.map((row) => (
                <div key={row.name} style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', fontSize: '0.78rem', color: '#5A4A42' }}>
                  <span style={{ fontWeight: 600 }}>{row.name}</span>
                  {row.isNew && (
                    <span style={{
                      background: '#C8E6C9', color: '#2E7D32', borderRadius: 4,
                      padding: '0 0.3rem', fontSize: '0.62rem', fontWeight: 800,
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                    }}>
                      New
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', color: '#7a6a62', fontSize: '0.74rem' }}>
                    {row.items == null ? '' : `${row.items} ${row.items === 1 ? 'item' : 'items'}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Relationships section ────────────────────────────────────────────────────

// The cards stay non-gating (auto-accept is the default, AJ 2026-08-18): a
// product's Keep box arrives ticked and Save with nothing touched confirms
// exactly what the host's accept-all plan already held. Unticking is the one
// decision offered — decline a pending suggestion (the product stays its own
// item) or detach a vendor product where planChanges can honor it. Products
// with neither lever (combined joins already written, context-only partners)
// render without a control, and the whole section is display-only when the
// host provides no decision handlers.
function RelationshipsSection({
  groups,
  uncheckedOptions,
  uncheckedSuggestions,
  onToggleOption,
  onToggleSuggestion,
}) {
  if (!groups || groups.length === 0) return null;
  const productCount = groups.reduce((sum, group) => sum + group.products.length, 0);

  const declinableSuggestionIds = (product) => (
    typeof onToggleSuggestion === 'function' && Array.isArray(product.suggestionIds)
      ? product.suggestionIds.filter((id) => id != null)
      : []
  );
  const optionDetachable = (product) => (
    typeof onToggleOption === 'function'
      && product.optionId != null
      && product.optionActionable === true
  );
  const hasControls = groups.some((group) => (group.products || []).some((product) => (
    declinableSuggestionIds(product).length > 0 || optionDetachable(product)
  )));

  return (
    <div
      data-testid="relationships-section"
      style={{
        border: '1px solid #E8DDD4',
        borderRadius: 12,
        marginBottom: '1rem',
        background: '#FBF8F5',
        padding: '1rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.92rem', fontWeight: 800, color: '#5A4A42' }}>
          Products that appear to be related for purchasing decisions
        </div>
        <div style={{ fontSize: '0.72rem', color: '#8B7B73', fontWeight: 700 }}>
          {groups.length} {groups.length === 1 ? 'group' : 'groups'} · {productCount} {productCount === 1 ? 'product' : 'products'}
        </div>
      </div>
      <div style={{ fontSize: '0.78rem', color: '#7a6a62', marginTop: 3, lineHeight: 1.4 }}>
        Countertop grouped products that may satisfy the same inventory need.
        {hasControls && ' Uncheck Keep on anything you’d rather keep as a separate item.'}
      </div>
      <div className="iat-relationship-grid" style={{ marginTop: '0.75rem' }}>
        {groups.map((group) => (
          <div
            key={group.key}
            data-testid="relationship-card"
            style={{
              border: '1px solid #E6D8CC',
              borderRadius: 10,
              background: '#fff',
              overflow: 'hidden',
              boxShadow: '0 1px 2px rgba(90, 74, 66, 0.04)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
              padding: '0.55rem 0.7rem', background: '#FFF9F3', borderBottom: '1px solid #EFE4DA',
            }}>
              <span style={{ fontSize: '0.76rem', fontWeight: 800, color: '#5A4A42' }}>
                {group.products.length} {group.products.length === 1 ? 'way' : 'ways'} to buy
              </span>
              <span style={{
                padding: '0.12rem 0.45rem', borderRadius: 999, background: '#EAF0F3',
                color: '#526B7C', fontSize: '0.66rem', fontWeight: 800,
              }}>
                Related
              </span>
            </div>
            <div role="list">
              {group.products.map((product, index) => {
                const suggestionIds = declinableSuggestionIds(product);
                // A product can carry both levers (a folded partner that is
                // also a vendor product of its own item). The suggestion is
                // the edge that put it in this group, so declining dismisses
                // the pending link; detaching would split the product's OWN
                // item, which is not what "not related" means.
                const declinesSuggestions = suggestionIds.length > 0;
                const detachesOption = !declinesSuggestions && optionDetachable(product);
                const actionable = declinesSuggestions || detachesOption;
                const kept = declinesSuggestions
                  ? !suggestionIds.some((id) => uncheckedSuggestions && uncheckedSuggestions.has(String(id)))
                  : !(detachesOption && uncheckedOptions && uncheckedOptions.has(String(product.optionId)));
                const handleKeep = (checked) => {
                  if (declinesSuggestions) suggestionIds.forEach((id) => onToggleSuggestion(id, checked));
                  else if (detachesOption) onToggleOption(product.optionId, checked);
                };
                return (
                  <div
                    key={product.key}
                    role="listitem"
                    data-testid="relationship-product"
                    style={{
                      padding: '0.58rem 0.7rem',
                      borderTop: index === 0 ? 'none' : '1px solid #F2E9E2',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.45rem', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.3,
                        color: kept ? '#5A4A42' : '#9A8A82',
                      }}>
                        {product.name}
                      </span>
                      {product.countUnit && (
                        <span style={{
                          color: '#7A6A62', background: '#F4EEE9', borderRadius: 999,
                          padding: '0.08rem 0.4rem', fontSize: '0.65rem', fontWeight: 700,
                          whiteSpace: 'nowrap', opacity: kept ? 1 : 0.6,
                        }}>
                          counted in {product.countUnit}
                        </span>
                      )}
                      {!kept && (
                        <span data-testid="relationship-declined-note" style={{
                          color: '#8B7B73', background: '#F1EAE4', borderRadius: 999,
                          padding: '0.08rem 0.4rem', fontSize: '0.65rem', fontWeight: 700,
                          whiteSpace: 'nowrap',
                        }}>
                          Kept separate
                        </span>
                      )}
                      {actionable && (
                        <label style={{
                          marginLeft: 'auto', display: 'inline-flex', alignItems: 'center',
                          gap: '0.28rem', color: '#5A4A42', fontSize: '0.7rem', fontWeight: 700,
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}>
                          <input
                            type="checkbox"
                            checked={kept}
                            onChange={(e) => handleKeep(e.target.checked)}
                            aria-label={`Keep ${product.name} in this group`}
                          />
                          Keep
                        </label>
                      )}
                    </div>
                    {product.details && (
                      <div style={{
                        color: '#7A6A62', fontSize: '0.72rem', lineHeight: 1.35,
                        marginTop: '0.12rem', opacity: kept ? 1 : 0.6,
                      }}>
                        {product.details}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── The review ─// ─── The review ───────────────────────────────────────────────────────────────

export default function ImportApprovalTable({
  items = [],
  onUpdateItem,
  onReclassifyItem,
  onImport,
  onBack,
  isImporting = false,
  importButtonLabel,
  rowNoun = 'item',
  storeId,
  countTargetsMap = {},
  onToggleCountTarget,
  hidePriceColumn = false,
  // Optional per-flow override for the EDITOR drawer's field set. When
  // provided, replaces the default editable column set entirely so each caller
  // (menu importer vs. document importer) can expose exactly the fields it
  // needs. The compact table's columns are fixed — Item / Vendor / Unit /
  // Price — so a wide field set widens the drawer, never the table.
  // Each entry: { key, label, type: 'text'|'number'|'decimal'|'vendor', minWidth }.
  columns: columnsOverride,
  // Optional node rendered between the review sections and the action bar —
  // used by the document import flow to surface vendor contact info found on
  // the uploaded document.
  footerExtra = null,
  // Opt-in: offer "⇄ Another way to buy it", which lands the line as a
  // purchase option on the matched item instead of a second countable item.
  // Only flows whose save path writes purchase_options should turn this on.
  allowVendorProduct = false,
  // (item) => ({ existingVendorCount, existingOptionCount, countUnit }) — the
  // extra facts the landing sentence needs about the matched item.
  landingContextFor,
  // Opt-in: the help-page id (see utils/helpTopics.js) for the flow hosting
  // this table. When set, the review-workflow concepts get ⓘ help triggers
  // that open Counterintelligence with that page's context. Absent → no triggers.
  helpPage,
  // What the import session found, for the summary above the table. Absent for
  // the flows that reuse this table outside a document import (a menu import, a
  // price-update review), which render exactly as they did before.
  //   { itemsFound, vendors: string[], newVendors: string[],
  //     vendorBreakdown: [{ name, items, isNew }], createdItemIds: number[],
  //     note: string }
  // `createdItemIds` is the session-wide list of items this import created —
  // the host holds it because a multi-file import is several server runs and
  // only the host sees all of them.
  importSummary = null,
  // The relationships the import found, shown in a second section under the
  // product table: { groups } where groups is the relatedReviewGroups output
  // from ImportConsolidationReview. Connected edges are folded into one visual
  // group here; the original decisions remain untouched for saving.
  relationships = null,
  // Called with { uncheckedParentIds, uncheckedOptionIds, uncheckedSuggestionIds }
  // whenever a relationship decision changes; the host feeds these into
  // planChanges at save time.
  onRelationshipsChange,
}) {
  const [search, setSearch] = useState('');
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [savePending, setSavePending] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [uncheckedOptions, setUncheckedOptions] = useState(() => new Set());
  const [uncheckedSuggestions, setUncheckedSuggestions] = useState(() => new Set());
  // What each row was before its Import box was unticked, so re-ticking
  // restores the exact classification instead of guessing.
  const restoreClassRef = useRef(new Map());

  // Menu-derived imports have no prices yet, so drop the price column and
  // collapse any lingering price_change classifications into "new".
  const columns = useMemo(
    () => {
      const base = Array.isArray(columnsOverride) && columnsOverride.length > 0
        ? columnsOverride
        : ALL_EDITABLE_COLUMNS;
      return hidePriceColumn ? base.filter((c) => c.key !== 'case_price') : base;
    },
    [hidePriceColumn, columnsOverride],
  );

  const classify = useCallback((item) => {
    const cls = effectiveClassification(item);
    if (hidePriceColumn && cls === 'price_change') return 'new';
    return cls || 'new';
  }, [hidePriceColumn]);

  // Per-row context for the landing sentence, memoised so a table of hundreds
  // of rows does not re-derive it on every keystroke in the search box.
  const landingContext = useCallback(
    (item) => (typeof landingContextFor === 'function' ? (landingContextFor(item) || {}) : {}),
    [landingContextFor],
  );

  // One landing per row, shared by the badge, the warning line, the drawer
  // and the counts — so every surface answers from the same description.
  const describedRows = useMemo(
    () => items.map((item) => ({
      item,
      key: reviewRowKey(item),
      classification: classify(item),
      landing: describeLanding(item, landingContext(item)),
    })),
    [items, classify, landingContext],
  );

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matches = (row) => {
      if (!q) return true;
      return columns.some((col) => String(row.item[col.key] ?? '').toLowerCase().includes(q));
    };
    const filtered = describedRows.filter(matches);
    // Exceptions first, so attention lands where it belongs; routine rows
    // keep their source order below them. Relationship warnings are
    // informational and never promote a row to "needs attention".
    const attention = filtered.filter((row) => realWarningsForLanding(row.landing).length > 0);
    const routine = filtered.filter((row) => realWarningsForLanding(row.landing).length === 0);
    return [...attention, ...routine];
  }, [describedRows, search, columns]);

  // What approving right now would actually do to the catalog.
  const landingSummary = useMemo(
    () => summarizeLandings(items, landingContext),
    [items, landingContext],
  );
  const realWarningCount = useMemo(
    () => describedRows.filter((row) => realWarningsForLanding(row.landing).length > 0).length,
    [describedRows],
  );

  const totalCount = items.length;
  const importCount = useMemo(
    () => items.filter((i) => !isDeclinedImportRow(i)).length,
    [items],
  );
  const rowNounPlural = `${rowNoun}${rowNoun.endsWith('s') ? '' : 's'}`;
  const nounFor = (n) => (n === 1 ? rowNoun : rowNounPlural);

  const importLabel = importButtonLabel ?? `Import ${importCount} ${nounFor(importCount)}`;
  const saving = isImporting || savePending;

  // The host save handlers intentionally reject when any relationship write
  // fails so the review stays open and can be retried. Catch that rejection
  // here, inside the modal, rather than relying on a page-level toast hidden
  // behind the overlay (which made the Save button appear to do nothing).
  const handleImportClick = useCallback(async () => {
    if (saving || typeof onImport !== 'function') return;
    setSaveError('');
    setSavePending(true);
    try {
      await onImport();
    } catch (error) {
      const detail = String(error?.message || '').trim();
      const retryGuidance = /try\b.*\bagain/i.test(detail) ? '' : ' Try Save again.';
      setSaveError(
        `We couldn't finish saving this import.${detail ? ` ${detail}` : ''} Your review is still open.${retryGuidance}`,
      );
    } finally {
      setSavePending(false);
    }
  }, [onImport, saving]);

  const toggleExpanded = useCallback((key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // The Import checkbox answers ONE question — is this row written or not —
  // so unticking is a disapproval and re-ticking restores what the row was.
  const toggleImport = useCallback((item, checked) => {
    if (!onReclassifyItem) return;
    const key = reviewRowKey(item);
    if (!checked) {
      const current = classify(item);
      if (current !== CLASSIFICATIONS.REJECTED && current !== CLASSIFICATIONS.UNCHANGED) {
        restoreClassRef.current.set(key, current);
      }
      onReclassifyItem(key, CLASSIFICATIONS.REJECTED);
      return;
    }
    const remembered = restoreClassRef.current.get(key);
    const fallback = item._existingItem && !hidePriceColumn
      ? CLASSIFICATIONS.PRICE_CHANGE
      : 'new';
    onReclassifyItem(key, remembered || fallback);
  }, [onReclassifyItem, classify, hidePriceColumn]);

  const visibleImportState = useMemo(() => {
    if (visibleRows.length === 0) return 'none';
    let on = 0;
    for (const row of visibleRows) if (!isDeclinedImportRow(row.item)) on += 1;
    if (on === 0) return 'none';
    if (on === visibleRows.length) return 'all';
    return 'some';
  }, [visibleRows]);

  const toggleImportAll = useCallback((checked) => {
    for (const row of visibleRows) {
      const importing = !isDeclinedImportRow(row.item);
      if (importing !== checked) toggleImport(row.item, checked);
    }
  }, [visibleRows, toggleImport]);

  // Relationship decisions — reported upward on every change so the host can
  // feed planChanges at save time without the modal knowing about Supabase.
  const reportRelationships = useCallback((options, suggestions) => {
    if (typeof onRelationshipsChange === 'function') {
      onRelationshipsChange({
        uncheckedParentIds: [],
        uncheckedOptionIds: [...options],
        uncheckedSuggestionIds: [...suggestions],
      });
    }
  }, [onRelationshipsChange]);

  const toggleRelationshipOption = useCallback((optionId, kept) => {
    setUncheckedOptions((prev) => {
      const next = new Set(prev);
      if (kept) next.delete(String(optionId)); else next.add(String(optionId));
      reportRelationships(next, uncheckedSuggestions);
      return next;
    });
  }, [reportRelationships, uncheckedSuggestions]);

  const toggleRelationshipSuggestion = useCallback((suggestionId, accepted) => {
    setUncheckedSuggestions((prev) => {
      const next = new Set(prev);
      if (accepted) next.delete(String(suggestionId)); else next.add(String(suggestionId));
      reportRelationships(uncheckedOptions, next);
      return next;
    });
  }, [reportRelationships, uncheckedOptions]);

  const relationshipGroups = relationships && Array.isArray(relationships.groups)
    ? relationships.groups
    : [];
  // Which partner items this import created, versus which the store already
  // had. One lookup, shared by the cards, the summary tiles and the footer, so
  // the three cannot report different stories about the same links.
  const originOf = useMemo(
    () => importOriginLookup(items, importSummary && importSummary.createdItemIds),
    [items, importSummary],
  );
  const relationshipCards = useMemo(
    () => relationshipGroups
      .map((group) => relationshipCardModel(group, originOf))
      .filter((c) => c.links.length > 0 || c.members.length >= 2),
    [relationshipGroups, originOf],
  );
  const relationshipDisplayGroups = useMemo(
    () => groupRelationshipCards(relationshipCards),
    [relationshipCards],
  );
  const summaryNumbers = useMemo(
    () => (importSummary
      ? buildImportSummary({
        items,
        contextFor: landingContextFor,
        relationshipGroups,
        createdItemIds: importSummary.createdItemIds,
        vendors: importSummary.vendors,
        newVendors: importSummary.newVendors,
        itemsFound: importSummary.itemsFound,
      })
      : null),
    [importSummary, items, landingContextFor, relationshipGroups],
  );
  // How many relationships Save will actually confirm. The host rebuilds its
  // accept-all plan around the decisions reported from this table
  // (extras.relationshipDecisions → planChanges), so a declined suggestion is
  // dismissed rather than created and drops out of this count. Combined links
  // are already written and always count; a member-only grouping counts while
  // at least two of its vendor products stay kept.
  const relationshipWillCreate = useMemo(() => {
    let count = 0;
    const counted = new Set();
    relationshipCards.forEach((card) => {
      if (card.links.length === 0) {
        const keptMembers = card.members
          .filter((member) => !uncheckedOptions.has(String(member.id))).length;
        if (keptMembers >= 2) count += 1;
        return;
      }
      card.links.forEach((link) => {
        if (link.kind === 'combined') count += 1;
        else if (link.kind === 'suggestion') {
          const id = String(link.suggestionId);
          if (counted.has(id)) return;
          counted.add(id);
          if (!uncheckedSuggestions.has(id)) count += 1;
        }
      });
    });
    return count;
  }, [relationshipCards, uncheckedOptions, uncheckedSuggestions]);

  const showPrice = !hidePriceColumn;
  const colCount = 4 + (showPrice ? 1 : 0) + 1;

  const counts = landingSummary.counts;
  const headerParts = [];
  headerParts.push(`${counts.new_item} new`);
  if (showPrice) headerParts.push(`${counts.update_price} price change${counts.update_price === 1 ? '' : 's'}`);
  if (allowVendorProduct || counts.new_option > 0) headerParts.push(`${counts.new_option} new vendor option${counts.new_option === 1 ? '' : 's'}`);
  headerParts.push(`${counts.skip + counts.reject} skipped`);

  const thStyle = {
    padding: '0.45rem 0.5rem', textAlign: 'left', color: '#5A4A42', fontWeight: 700,
    fontSize: '0.75rem', whiteSpace: 'nowrap', background: '#FAF3EC',
    position: 'sticky', top: 0, zIndex: 2, textTransform: 'uppercase', letterSpacing: '0.03em',
  };

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* What the import found — one account, above the evidence for it */}
      {importSummary && summaryNumbers && (
        <ImportSummaryHeader
          summary={importSummary}
          numbers={summaryNumbers}
          showPrice={showPrice}
          rowNounPlural={rowNounPlural}
        />
      )}

      {/* Section 1: the products this import writes */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#5A4A42' }}>
          {`${rowNounPlural.charAt(0).toUpperCase()}${rowNounPlural.slice(1)} to import`}
        </h3>
        {helpPage && <HelpInfoButton helpTopicId="import.review_actions" page={helpPage} section="review" />}
        <span data-testid="products-summary" style={{ fontSize: '0.78rem', color: '#7a6a62' }}>
          {totalCount} {nounFor(totalCount)} · {headerParts.join(' · ')}
        </span>
      </div>

      {/* Search bar */}
      <div style={{ position: 'relative', marginBottom: '0.6rem' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Search ${rowNounPlural}…`}
          style={{
            width: '100%', padding: '0.4rem 0.75rem 0.4rem 2rem',
            border: '1px solid #D8C8B8', borderRadius: 8, fontSize: '0.85rem',
            background: '#fff', color: '#5A4A42', outline: 'none', boxSizing: 'border-box',
          }}
        />
        <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#B0A098', fontSize: '0.78rem' }}>🔍</span>
      </div>

      {/* Table */}
      <div style={{ border: '1px solid #EFE4DA', borderRadius: 10, overflow: 'hidden', marginBottom: '0.9rem' }}>
        <div style={{ overflowX: 'auto', maxHeight: '58vh', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #E8DBCE' }}>
                <th style={{ ...thStyle, textAlign: 'center', width: 40 }}>
                  <input
                    type="checkbox"
                    checked={visibleImportState === 'all' && visibleRows.length > 0}
                    ref={(el) => { if (el) el.indeterminate = visibleImportState === 'some'; }}
                    disabled={visibleRows.length === 0 || !onReclassifyItem}
                    onChange={(e) => toggleImportAll(e.target.checked)}
                    style={{ cursor: visibleRows.length === 0 ? 'not-allowed' : 'pointer', accentColor: '#526B7C', width: 15, height: 15 }}
                    aria-label="Import all"
                    title="Import all"
                  />
                </th>
                <th style={thStyle}>Item</th>
                <th className="iat-col-secondary" style={thStyle}>Vendor</th>
                <th className="iat-col-secondary" style={thStyle}>Purchase unit</th>
                {showPrice && <th className="iat-col-secondary" style={thStyle}>Price per purchase unit</th>}
                <th style={{ ...thStyle, textAlign: 'right' }}>
                  <span aria-hidden="true"> </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {totalCount === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ padding: '2rem', textAlign: 'center', color: '#9a8a82' }}>
                    No {rowNounPlural} to review.
                  </td>
                </tr>
              )}
              {visibleRows.map((row) => (
                <ProductRow
                  key={row.key}
                  item={row.item}
                  landing={row.landing}
                  classification={row.classification}
                  expanded={expandedKeys.has(row.key)}
                  onToggleExpanded={toggleExpanded}
                  onToggleImport={toggleImport}
                  onUpdateItem={onUpdateItem}
                  onReclassify={onReclassifyItem}
                  storeId={storeId}
                  columns={columns}
                  showPrice={showPrice}
                  countTargets={countTargetsMap[row.item.id] || []}
                  onToggleCountTarget={onToggleCountTarget}
                  landingContext={landingContext(row.item)}
                  allowVendorProduct={allowVendorProduct}
                  helpPage={helpPage}
                  colCount={colCount}
                />
              ))}
              {search && visibleRows.length === 0 && totalCount > 0 && (
                <tr>
                  <td colSpan={colCount} style={{ padding: '2rem', textAlign: 'center', color: '#9a8a82' }}>
                    No {rowNounPlural} match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2: the relationships Countertop noticed — non-gating, but a
          proposed link can be declined here before Save confirms it. Controls
          appear only when the host listens for the decisions; otherwise the
          section stays purely informational. */}
      <RelationshipsSection
        groups={relationshipDisplayGroups}
        uncheckedOptions={uncheckedOptions}
        uncheckedSuggestions={uncheckedSuggestions}
        onToggleOption={typeof onRelationshipsChange === 'function' ? toggleRelationshipOption : null}
        onToggleSuggestion={typeof onRelationshipsChange === 'function' ? toggleRelationshipSuggestion : null}
      />

      {/* Lower portion — e.g. vendor contact captured from the imported document */}
      {footerExtra}

      {saveError && (
        <div
          role="alert"
          data-testid="import-save-error"
          style={{
            marginBottom: '0.75rem', padding: '0.7rem 0.85rem',
            border: '1px solid #F1B0AA', borderRadius: 8,
            background: '#FDECEA', color: '#8F251D',
            fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.4,
          }}
        >
          {saveError}
        </div>
      )}

      {/* Final action area: part of the document flow, reached after review. */}
      <div
        className="iat-actions"
        data-testid="import-actions"
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap',
          gap: '0.75rem', marginTop: '1.15rem', paddingTop: '1rem', borderTop: '1px solid #E8DDD4',
        }}
      >
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            disabled={saving}
            style={{
              background: '#fff', border: '1px solid #D8C8B8', borderRadius: 8, color: '#526B7C',
              fontSize: '0.86rem', cursor: saving ? 'not-allowed' : 'pointer',
              padding: '0.5rem 0.85rem', fontWeight: 700,
            }}
          >
            Cancel import
          </button>
        )}
        <div className="iat-actions-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginLeft: 'auto', flexWrap: 'wrap' }}>
          {/* What approving does, in one sentence, right beside the button. */}
          <span data-testid="landing-summary" style={{ color: '#5A4A42', fontSize: '0.82rem', textAlign: 'right' }}>
            {importCount} {nounFor(importCount)} will be imported
            {relationshipWillCreate > 0 && (
              <> · {relationshipWillCreate} relationship{relationshipWillCreate === 1 ? '' : 's'} will be created</>
            )}
            {realWarningCount > 0 && (
              <span style={{ display: 'block', color: '#E65100', fontWeight: 600, fontSize: '0.78rem' }}>
                ⚠ {realWarningCount} row{realWarningCount !== 1 ? 's need' : ' needs'} a second look
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={handleImportClick}
            disabled={saving}
            style={{
              background: saving ? '#B0A098' : '#526B7C',
              color: '#fff', border: 'none', borderRadius: 8,
              padding: '0.55rem 1.4rem', fontSize: '0.9rem', fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.4rem',
            }}
          >
            {saving ? (
              <>
                <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                Saving…
              </>
            ) : importLabel}
          </button>
          {/* Onboarding add-items flow: explain what the approve button builds. */}
          {helpPage === 'add_items' && (
            <HelpInfoButton helpTopicId="additems.approve_build" page={helpPage} section="review" />
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        /* Phones: the vendor/unit/price columns fold into the meta line under
           each item name, so the table FITS the screen instead of scrolling
           sideways (horizontal scroll is explicitly not the design here). */
        .iat-meta-inline { display: none; }
        .iat-relationship-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
          gap: 0.65rem;
          align-items: start;
        }
        @media (max-width: 640px) {
          .iat-col-secondary { display: none; }
          .iat-meta-inline { display: block; }
          .iat-actions { align-items: stretch !important; }
          .iat-actions-primary { margin-left: 0 !important; align-items: stretch !important; }
          .iat-actions-primary [data-testid="landing-summary"] { flex: 1 1 100%; text-align: left !important; }
        }
      `}</style>
    </div>
  );
}
